import React, { useState } from 'react';
import { PopoverProps, TagProps, Tag, Popover } from '@fx-ui/fine-design';
import classNames from 'classnames';

interface ValueTagProps {
    tagOptions?: TagProps;
    /** 不传值则无弹窗 */
    popoverOptions?: PopoverProps;
    icon?: React.ReactElement;
    name?: React.ReactElement | React.ReactNode;
    valueKey?: string | number;
}

/**
 * 值标签
 * @description 用于 XXXSelectDialog 组件中，展示选中项
 */
const ValueTag: React.FC<ValueTagProps> = ({
    tagOptions,
    popoverOptions,
    icon,
    name,
    valueKey
}) => {
    const [popoverInteractive, setPopoverInteractive] = useState(false);
    const tagCommonOptions: TagProps = {
        shape: 'roundedRec',
        size: 'large',
        ...tagOptions,
        className: classNames('fx-value-tag', tagOptions?.className)
    };

    // 直接返回原生节点，避免在 ValueTag 内声明子组件后因组件类型变化导致 Popover 触发节点反复挂载
    const renderTagContent = (props?: React.HTMLAttributes<HTMLDivElement>) => (
        <div style={{ display: 'flex', alignItems: 'center' }} {...props}>
            {icon &&
                React.cloneElement(icon, {
                    className: classNames('tag-prefix', icon.props.className)
                })}
            <div className="text-wrapper">{name}</div>
        </div>
    );

    if (popoverOptions) {
        return (
            <Tag {...tagCommonOptions}>
                <Popover
                    key={valueKey}
                    trigger="hover"
                    placement="bottom-left"
                    hasPadding={false}
                    arrow={false}
                    mouseEnterDelay={1}
                    {...popoverOptions}
                    overlayStyle={{
                        /*
                         * Wujie 子应用中的 Popover 会先以错误坐标渲染首个非零尺寸帧，该帧覆盖触发标签，
                         * 使鼠标命中元素从标签切换为浮层并触发标签的 mouseleave；浮层关闭后鼠标重新
                         * 命中标签，经过 mouseEnterDelay 再次打开，最终形成反复开关的循环。
                         * 鼠标停留标签时禁用浮层的鼠标事件，避免首帧抢占标签的鼠标命中目标；
                         * 实际移出标签后恢复浮层交互，由 Popover 处理移入卡片保持显示及移出关闭。
                         * 每次鼠标进入标签都先复位，避免未等到 mouseEnterDelay 就划走时残留可交互状态
                         * （浮层未打开不会触发 onAfterPopupHide），使下次悬停时首帧再次抢占标签
                         */
                        pointerEvents: popoverInteractive ? 'auto' : 'none',
                        ...popoverOptions.overlayStyle
                    }}
                    onAfterPopupHide={() => {
                        setPopoverInteractive(false);
                        popoverOptions.onAfterPopupHide?.();
                    }}
                >
                    {renderTagContent({
                        onMouseEnter: () => setPopoverInteractive(false),
                        onMouseLeave: () => setPopoverInteractive(true)
                    })}
                </Popover>
            </Tag>
        );
    }

    return <Tag {...tagCommonOptions}>{renderTagContent()}</Tag>;
};

export default ValueTag;
